import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectAdminToken } from "../../admin/sessionSlice";
import "./stage4.css";
import { fireGameAlert } from "./gameAlert";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  markStageCompleted,
  normalizePlayerName,
  saveGameStageAndReport,
  setStageActive,
  updateExternalReport,
} from "../../functions/stageReportSync";
import {
  RIDDLES,
  POINTS_PER_RIDDLE,
  checkRiddleAnswer,
  shuffleIds,
} from "./riddleData";

import stage4Bg from "./img/stage1bg.png";
import title4Img from "./img/title4.png";
import h1 from "./img/h1.png";
import h2 from "./img/h2.png";
import h3 from "./img/h3.png";
import h4 from "./img/h4.png";
import h5 from "./img/h5.png";
import h6 from "./img/h6.png";
import h7 from "./img/h7.png";
import h8 from "./img/h8.png";
import h9 from "./img/h9.png";
import h10 from "./img/h10.png";

const STAGE_NUM = 4;

const HEADS = [
  { id: 1, src: h1 },
  { id: 2, src: h2 },
  { id: 3, src: h3 },
  { id: 4, src: h4 },
  { id: 5, src: h5 },
  { id: 6, src: h6 },
  { id: 7, src: h7 },
  { id: 8, src: h8 },
  { id: 9, src: h9 },
  { id: 10, src: h10 },
];

// Mirrors the z-index values in stage4.css so hit-testing checks the
// topmost (visually frontmost) head first when bounding boxes overlap.
const HEAD_Z_INDEX = {
  1: 1,
  2: 2,
  3: 3,
  4: 4,
  5: 10,
  6: 9,
  7: 8,
  8: 7,
  9: 6,
  10: 5,
};

function parseClockToSeconds(clock) {
  const parts = String(clock ?? "00:00")
    .trim()
    .split(":")
    .map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

export default function Stage4() {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);

  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || null
  );
  const [dbReady, setDbReady] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [stage4Points, setStage4Points] = useState(0);
  const [displayTotalScore, setDisplayTotalScore] = useState(0);
  const [usedHeads, setUsedHeads] = useState(() => new Set());
  const [activeRiddle, setActiveRiddle] = useState(null);
  const [answerInput, setAnswerInput] = useState("");
  const [answerState, setAnswerState] = useState("idle");
  const [submitting, setSubmitting] = useState(false);

  const orderRef = useRef(shuffleIds(RIDDLES.length));
  const usedHeadsRef = useRef(new Set());
  const questionCountRef = useRef(0);
  const stage4PointsRef = useRef(0);
  const elapsedRef = useRef(0);
  const baseElapsedRef = useRef(0);
  const sessionStartRef = useRef(null);
  const otherStagesTotalRef = useRef(0);
  const submittedRef = useRef(false);
  const timerStartedRef = useRef(false);
  const demoRedirectTriggeredRef = useRef(false);
  const ravanaRef = useRef(null);
  const headPixelDataRef = useRef({});

  const storedUser = useMemo(() => getStoredUser(), []);
  const isDemoBypass = useMemo(() => {
    const role = String(storedUser.role || "").toLowerCase();
    const source = String(storedUser.source || "").toUpperCase();
    const sessionId = String(storedUser.sessionId || "");
    return (
      sessionId === "demobypass" ||
      role === "demobypass" ||
      source === "DEMO" ||
      sessionId.startsWith("demo")
    );
  }, [storedUser]);
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      backgroundImage: body.style.backgroundImage,
      backgroundSize: body.style.backgroundSize,
      backgroundRepeat: body.style.backgroundRepeat,
      backgroundPosition: body.style.backgroundPosition,
      backgroundAttachment: body.style.backgroundAttachment,
    };
    html.classList.add("stage4-lock");
    body.classList.remove("common-bg");
    body.classList.add("riddler-page");
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.backgroundImage = `url("${stage4Bg}")`;
    body.style.backgroundSize = "100% 100%";
    body.style.backgroundRepeat = "no-repeat";
    body.style.backgroundPosition = "center center";
    body.style.backgroundAttachment = "fixed";
    return () => {
      html.classList.remove("stage4-lock");
      body.classList.remove("riddler-page");
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.backgroundImage = prev.backgroundImage;
      body.style.backgroundSize = prev.backgroundSize;
      body.style.backgroundRepeat = prev.backgroundRepeat;
      body.style.backgroundPosition = prev.backgroundPosition;
      body.style.backgroundAttachment = prev.backgroundAttachment;
    };
  }, []);

  useEffect(() => {
    stage4PointsRef.current = stage4Points;
    setDisplayTotalScore(otherStagesTotalRef.current + stage4Points);
  }, [stage4Points]);

  // Preload each head image onto an offscreen canvas so clicks can be
  // hit-tested against actual pixel opacity instead of the bounding box.
  useEffect(() => {
    let cancelled = false;
    HEADS.forEach((head) => {
      const img = new Image();
      img.onload = () => {
        if (cancelled) return;
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        headPixelDataRef.current[head.id] = {
          ctx,
          width: img.naturalWidth,
          height: img.naturalHeight,
        };
      };
      img.src = head.src;
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!dbReady || timerStartedRef.current) return undefined;
    timerStartedRef.current = true;
    sessionStartRef.current = Date.now();
    const tick = setInterval(() => {
      const sessionSec = Math.floor(
        (Date.now() - sessionStartRef.current) / 1000
      );
      const elapsed = baseElapsedRef.current + sessionSec;
      elapsedRef.current = elapsed;
      setElapsedSec(elapsed);
    }, 1000);
    return () => clearInterval(tick);
  }, [dbReady]);

  useEffect(() => {
    let cancelled = false;

    async function loadStageFromDb() {
      const uid = storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;

      if (isDemoBypass || !backendBase || !adminToken) {
        if (!cancelled) setDbReady(true);
        return;
      }
      if (!uid || !sessionId || !organizationId) {
        if (!cancelled) setDbReady(true);
        return;
      }

      try {
        let rowId = sessionStorage.getItem(STAGE_ROW_KEY);
        if (!rowId) {
          const startRes = await fetch(`${backendBase}/welcomeStageStart`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId,
              organizationId,
              email: storedUser.email || "",
              emp_id: storedUser.employeeId || storedUser.emp_id || "",
              name: normalizePlayerName(storedUser),
              user_type: storedUser.user_type || storedUser.userType || "",
            }),
          });
          const startData = await startRes.json().catch(() => ({}));
          if (startRes.ok && startData.success && startData.id != null) {
            rowId = String(startData.id);
            sessionStorage.setItem(STAGE_ROW_KEY, rowId);
          }
        }

        if (!rowId) {
          if (!cancelled) setDbReady(true);
          return;
        }
        if (!cancelled) setStageRowId(rowId);

        const stageRes = await fetch(`${backendBase}/getGameStage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId,
            organizationId,
            stageId: rowId,
          }),
        });
        const stageData = await stageRes.json().catch(() => ({}));
        if (cancelled) return;

        if (stageRes.ok && stageData.success && stageData.stage) {
          const st = stageData.stage;
          hydrateCompletedStagesFromRecord(st);
          const resumeRoute = getRedirectForPlayedStage(st, STAGE_NUM);
          if (resumeRoute) {
            navigate(resumeRoute, { replace: true });
            return;
          }
          const dbTimeSec = parseClockToSeconds(st.time);
          const dbTotal = Number(st.total_score) || 0;
          const dbStage4 = Number(st.stage4) || 0;
          baseElapsedRef.current = dbTimeSec;
          elapsedRef.current = dbTimeSec;
          setElapsedSec(dbTimeSec);
          otherStagesTotalRef.current = Math.max(0, dbTotal - dbStage4);
          stage4PointsRef.current = dbStage4;
          setStage4Points(dbStage4);
          setDisplayTotalScore(dbTotal);
        }

        setStageActive(STAGE_NUM, storedUser);
        setDbReady(true);
      } catch (err) {
        console.error("Stage4 loadStageFromDb:", err);
        if (!cancelled) setDbReady(true);
      }
    }

    loadStageFromDb();
    return () => {
      cancelled = true;
    };
  }, [backendBase, adminToken, isDemoBypass, storedUser, navigate]);

  const finishStage = useCallback(async () => {
    if (submitting || submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);

    const uid = storedUser.userId || storedUser.userid || storedUser.id;
    const sessionId = storedUser.sessionId;
    const organizationId = storedUser.organizationId;
    const timeStr = formatSecondsToClock(elapsedRef.current);
    const stage4Score = stage4PointsRef.current;
    const stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
    const finalTotal = otherStagesTotalRef.current + stage4Score;

    if (!isDemoBypass && backendBase && adminToken && stageId) {
      try {
        const saveResult = await saveGameStageAndReport({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId,
          stageNumber: STAGE_NUM,
          score: stage4Score,
          time: timeStr,
          total_score: finalTotal,
          currentQues: RIDDLES.length,
        });
        if (saveResult.total_score != null) {
          setDisplayTotalScore(saveResult.total_score);
        }
        await fetch(`${backendBase}/welcomeStageComplete`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId,
            organizationId,
            stageId,
            time: timeStr,
            total_score: finalTotal,
            points: finalTotal,
          }),
        });
        await updateExternalReport({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          points: finalTotal,
          time: timeStr,
          stageId,
        });
      } catch (err) {
        console.error("Stage4 finish:", err);
      }
    }

    clearStageActive(STAGE_NUM);
    markStageCompleted(STAGE_NUM, storedUser);
    markStageCompleted(5, storedUser);

    if (isDemoBypass) {
      window.location.href =
        String(process.env.REACT_APP_BASE_URL || "").trim() || "/";
      return;
    }
    await new Promise((resolve) => setTimeout(resolve, 2000));
    navigate("/thankyou");
  }, [
    submitting,
    storedUser,
    isDemoBypass,
    backendBase,
    adminToken,
    stageRowId,
    navigate,
  ]);

  const openHead = useCallback(
    (headId) => {
      if (submitting || submittedRef.current) return;
      if (usedHeadsRef.current.has(headId)) return;
      if (activeRiddle) return;
      if (questionCountRef.current >= RIDDLES.length) return;

      usedHeadsRef.current.add(headId);
      setUsedHeads(new Set(usedHeadsRef.current));

      const riddleIndex = orderRef.current[questionCountRef.current];
      const riddle = RIDDLES[riddleIndex];
      setActiveRiddle(riddle);
      setAnswerInput("");
      setAnswerState("idle");
    },
    [activeRiddle, submitting]
  );

  const isPointOnHead = useCallback((headId, clientX, clientY) => {
    const wrap = ravanaRef.current;
    const pixelData = headPixelDataRef.current[headId];
    if (!wrap || !pixelData) return false;
    const btn = wrap.querySelector(`[data-head-id="${headId}"]`);
    if (!btn) return false;
    const rect = btn.getBoundingClientRect();
    if (
      clientX < rect.left ||
      clientX >= rect.right ||
      clientY < rect.top ||
      clientY >= rect.bottom ||
      rect.width === 0 ||
      rect.height === 0
    ) {
      return false;
    }
    const px = Math.min(
      pixelData.width - 1,
      Math.floor(((clientX - rect.left) / rect.width) * pixelData.width)
    );
    const py = Math.min(
      pixelData.height - 1,
      Math.floor(((clientY - rect.top) / rect.height) * pixelData.height)
    );
    const alpha = pixelData.ctx.getImageData(px, py, 1, 1).data[3];
    return alpha > 10;
  }, []);

  const handleRavanaClick = useCallback(
    (e) => {
      if (submitting || submittedRef.current || activeRiddle) return;

      // Keyboard activation (Enter/Space) reports click coords as 0, so
      // trust the focused button directly instead of pixel hit-testing.
      if (e.detail === 0) {
        const btn = e.target.closest(".stage4-head");
        const headId = btn ? Number(btn.dataset.headId) : null;
        if (headId && !usedHeadsRef.current.has(headId)) openHead(headId);
        return;
      }

      const candidates = HEADS.filter(
        (head) => !usedHeadsRef.current.has(head.id)
      ).sort((a, b) => HEAD_Z_INDEX[b.id] - HEAD_Z_INDEX[a.id]);

      for (const head of candidates) {
        if (isPointOnHead(head.id, e.clientX, e.clientY)) {
          openHead(head.id);
          return;
        }
      }
    },
    [activeRiddle, submitting, openHead, isPointOnHead]
  );

  const submitAnswer = useCallback(async () => {
    if (!activeRiddle || submitting) return;
    const value = answerInput.trim();
    if (!value) {
      fireGameAlert({ icon: "error", title: "Please fill the answer", text: "" });
      return;
    }

    const correct = checkRiddleAnswer(value, activeRiddle.answers);
    if (correct) {
      const nextPoints = stage4PointsRef.current + POINTS_PER_RIDDLE;
      stage4PointsRef.current = nextPoints;
      setStage4Points(nextPoints);
    }

    const nextCount = questionCountRef.current + 1;
    questionCountRef.current = nextCount;
    setActiveRiddle(null);
    setAnswerInput("");
    setAnswerState("idle");

    if (
      isDemoBypass &&
      nextCount === 1 &&
      !demoRedirectTriggeredRef.current
    ) {
      demoRedirectTriggeredRef.current = true;
      await fireGameAlert({
        icon: "info",
        text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
        confirmButtonText: "OK",
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
      window.location.assign("https://extramileplay.com/plans");
      return;
    }

    if (nextCount >= RIDDLES.length) {
      finishStage();
    }
  }, [activeRiddle, answerInput, submitting, isDemoBypass, finishStage]);

  const onAnswerChange = useCallback(
    (value) => {
      setAnswerInput(value);
      if (!activeRiddle) return;
      if (!value.trim()) {
        setAnswerState("idle");
        return;
      }
      setAnswerState(
        checkRiddleAnswer(value, activeRiddle.answers) ? "correct" : "wrong"
      );
    },
    [activeRiddle]
  );

  if (!dbReady) {
    return <div className="stage4-loading">Loading game…</div>;
  }

  const clock = formatSecondsToClock(elapsedSec);

  return (
    <div className="stage4-page">
      <div className="stage4-header">
        <img src={title4Img} alt="The Riddler" className="stage4-title-img" />
        <p className="stage4-instructions">
          Click on Ravaan to solve riddles and uncover simple sustainability tips
          for your home.
        </p>
      </div>

      <div className="stage4-hud">
        <div className="stage4-chip" aria-label={`Time ${clock}`}>
          Time: {clock}
        </div>
        <div className="stage4-chip" aria-label={`Points ${displayTotalScore}`}>
          Points: {displayTotalScore}
        </div>
      </div>

      <div className="stage4-ravana-wrap">
        <div className="stage4-ravana" ref={ravanaRef} onClick={handleRavanaClick}>
          {HEADS.map((head) => (
            <button
              key={head.id}
              type="button"
              data-head-id={head.id}
              className={`stage4-head stage4-head--${head.id}${
                usedHeads.has(head.id) ? " is-used" : ""
              }`}
              disabled={usedHeads.has(head.id) || Boolean(activeRiddle)}
              aria-label={`Ravana head ${head.id}`}
            >
              <img src={head.src} alt="" />
            </button>
          ))}
        </div>
      </div>

      {activeRiddle ? (
        <div className="stage4-modal" role="dialog" aria-modal="true">
          <div className="stage4-modal__card">
            <h5 className="stage4-modal__question">{activeRiddle.question}</h5>
            <input
              className={`stage4-modal__input is-${answerState}`}
              type="text"
              placeholder="Type here"
              value={answerInput}
              readOnly={answerState === "correct"}
              onChange={(e) => onAnswerChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitAnswer();
              }}
              autoFocus
            />
            <button
              type="button"
              className="stage4-modal__submit"
              onClick={submitAnswer}
            >
              Submit
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
